/**
 * Assign existing property rooms to an apartment (COL-225, opt-in model).
 *
 * There is no standalone "create room" flow in V2 — rooms are born from the
 * Add Property wizard. So "add rooms to an apartment" means linking existing
 * rooms of the same property via rooms.apartment_id. This dialog lists every
 * room of the property as a checkbox; rooms already in this apartment start
 * checked. Saving assigns the newly-checked rooms and unassigns the cleared
 * ones in one mutation.
 *
 * Rooms that currently belong to a *different* apartment are flagged: checking
 * one moves it here (a room can only belong to one apartment).
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useApartments,
  useAssignRoomsToApartment,
  usePropertyRooms,
} from "@/hooks/v2/useProperties";
import { formatEUR } from "@/lib/v2-format";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apartmentId: number;
  propertyId: number;
};

export function AssignRoomsDialog({
  open,
  onOpenChange,
  apartmentId,
  propertyId,
}: Props) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { data: rooms = [], isLoading } = usePropertyRooms(propertyId);
  const { data: apartments = [] } = useApartments(propertyId);
  const assignMut = useAssignRoomsToApartment();

  // Selected room ids (the desired final membership of THIS apartment).
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Seed the selection from rooms currently in this apartment whenever the
  // dialog opens or the room list refreshes.
  useEffect(() => {
    if (!open) return;
    const init = new Set<number>();
    for (const r of rooms) {
      if (r.apartment_id === apartmentId) init.add(r.id);
    }
    setSelected(init);
  }, [open, rooms, apartmentId]);

  const apartmentLabelById = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of apartments) m.set(a.id, a.label);
    return m;
  }, [apartments]);

  function toggle(roomId: number, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(roomId);
      else next.delete(roomId);
      return next;
    });
  }

  // Diff against the current DB state to compute what actually changed.
  const { assign, unassign } = useMemo(() => {
    const assign: number[] = [];
    const unassign: number[] = [];
    for (const r of rooms) {
      const isHere = r.apartment_id === apartmentId;
      const wantHere = selected.has(r.id);
      if (wantHere && !isHere) assign.push(r.id);
      else if (!wantHere && isHere) unassign.push(r.id);
    }
    return { assign, unassign };
  }, [rooms, selected, apartmentId]);

  const changeCount = assign.length + unassign.length;

  async function handleSave() {
    if (changeCount === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await assignMut.mutateAsync({
        apartmentId,
        propertyId,
        assign,
        unassign,
      });
      toast({
        title: t("admin.rooms.roomsUpdated", "Rooms updated"),
        description: t(
          "admin.rooms.assignedRemovedCount",
          "{{assigned}} assigned · {{removed}} removed."
        )
          .replace("{{assigned}}", String(assign.length))
          .replace("{{removed}}", String(unassign.length)),
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t("admin.rooms.couldNotUpdateRooms", "Could not update rooms"),
        description: formatSupabaseError(err),
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("admin.apartments.assignRooms", "Assign rooms")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.rooms.assignRoomsDesc",
              "Pick which of this property's rooms belong to this apartment. A room can only belong to one apartment — checking a room already in another apartment moves it here. Unchecking a room here unassigns it (it falls back to property-level resolution)."
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
          </div>
        ) : rooms.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t(
              "admin.rooms.noRoomsToAssign",
              "This property has no rooms to assign."
            )}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {rooms.map((room) => {
              const inOther =
                room.apartment_id != null && room.apartment_id !== apartmentId;
              const checked = selected.has(room.id);
              return (
                <li key={room.id}>
                  <label
                    className="flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
                    htmlFor={`assign-room-${room.id}`}
                  >
                    <Checkbox
                      id={`assign-room-${room.id}`}
                      checked={checked}
                      onCheckedChange={(v) => toggle(room.id, v === true)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {room.title}
                        </span>
                        {room.is_hidden && (
                          <Badge variant="outline" className="text-[10px]">
                            {t("common.hidden", "Hidden")}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          {formatEUR(room.price) ?? "—"}/
                          {t("admin.rooms.perMonthSuffix", "mo")}
                        </span>
                        {inOther && (
                          <span className="text-amber-600">
                            · {t("admin.rooms.inApartment", "in")}{" "}
                            {apartmentLabelById.get(room.apartment_id!) ??
                              t(
                                "admin.rooms.anotherApartment",
                                "another apartment"
                              )}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={assignMut.isPending}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={assignMut.isPending || changeCount === 0}
          >
            {assignMut.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.saving", "Saving…")}
              </>
            ) : changeCount === 0 ? (
              t("admin.rooms.noChanges", "No changes")
            ) : (
              `${t("common.save", "Save")} (${changeCount} ${
                changeCount === 1
                  ? t("admin.rooms.changeSingular", "change")
                  : t("admin.rooms.changePlural", "changes")
              })`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
